import { StrictMode } from 'react'
import * as Sentry from '@sentry/react'
import { StartClient } from '@tanstack/react-start/client'
import { hydrateRoot } from 'react-dom/client'
import { ErrorBoundary } from 'react-error-boundary'

import { ErrorFallback } from './components/ErrorFallback.js'
import { initializeSentry } from './integrations/sentry/client.js'

// Lazy load mock media module only in non-production environments
// This allows testing webcam UI in browsers that block media device access
// Enable via: ?mockMedia=true or window.enableMockMedia() in console
if (import.meta.env.MODE !== 'production') {
  import('./lib/mockMedia.js').then(({ initMockMedia }) => {
    initMockMedia()
  })
}

initializeSentry()

const app = (
  <ErrorBoundary
    fallbackRender={({ error, resetErrorBoundary }) => (
      <ErrorFallback error={error} resetErrorBoundary={resetErrorBoundary} />
    )}
    onError={(error, info) => {
      Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: info.componentStack,
          },
        },
      })
    }}
    onReset={() => window.location.reload()}
  >
    <StartClient />
  </ErrorBoundary>
)

/**
 * StrictMode is off by default and switched on with `VITE_STRICT_MODE=1`.
 *
 * It used to be commented out under a TODO about "Maximum update depth
 * exceeded", which made answering the question require editing this file -
 * so nobody did, for months. A flag costs the same and can be flipped by
 * whoever has a backend in front of them.
 *
 * **What is known, measured 2026-09-12** against `vite dev` with the flag on
 * and a deliberately unreachable Convex URL: `/`, `/phone-camera`,
 * `/license` and `/game/:id` all render with zero "Maximum update depth"
 * in the console.
 *
 * **Development mode is the only mode where that means anything**, which is
 * the trap this measurement fell into once before being redone: React only
 * double-invokes effects in dev, so the same sweep against `vite build`
 * output proves nothing at all about StrictMode. If you repeat it, repeat it
 * against `vite dev`.
 *
 * **It is still only the easy half.** Without a backend `/game/:id` stops at
 * the sign-in dialog, so the room's interior - the presence hook with its
 * fourteen effects, the video grid, the stats overlay - was never mounted.
 * The TODO may well be stale; this does not disprove it.
 *
 * **To settle it:** run with `VITE_STRICT_MODE=1` against a real deployment,
 * open a room, and watch the console. Clean means this default can flip and
 * the flag can go.
 */
const useStrictMode = import.meta.env.VITE_STRICT_MODE === '1'

hydrateRoot(document, useStrictMode ? <StrictMode>{app}</StrictMode> : app)
